export const DOMESTIC_COMBINED_JOURNEY_COMPATIBILITY_GROUPS = ['ATPCO', 'NDC', 'LCC'] as const
export const MAX_DOMESTIC_COMBINED_JOURNEYS = 1000
export const DOMESTIC_COMBINED_JOURNEY_SHORTLIST_SIZE = 48
export const MAX_DOMESTIC_COMBINED_JOURNEYS_PER_CARRIER = 16

export type DomesticCombinedJourneyCompatibilityGroup = typeof DOMESTIC_COMBINED_JOURNEY_COMPATIBILITY_GROUPS[number]

export function getTwoOneWayCompatibilityGroup(
  priceType?: string,
): DomesticCombinedJourneyCompatibilityGroup {
  if (priceType === 'NDC' || priceType === 'LCC') {
    return priceType
  }

  return 'ATPCO'
}

function getCombinedJourneyPrice(journey: App.FlightCombinedJourney): number {
  return (journey.departingJourney.price.all?.totalFare ?? journey.departingJourney.price.adult?.totalFare ?? 0) +
    (journey.returningJourney.price.all?.totalFare ?? journey.returningJourney.price.adult?.totalFare ?? 0)
}

function getCombinedJourneyStops(journey: App.FlightCombinedJourney): number {
  return journey.departingJourney.flightGroup.numberOfStops + journey.returningJourney.flightGroup.numberOfStops
}

function getCombinedJourneyDuration(journey: App.FlightCombinedJourney): number {
  const departingDuration = journey.departingJourney.flightGroup.totalTravelDuration
  const returningDuration = journey.returningJourney.flightGroup.totalTravelDuration
  return departingDuration.hours * 60 + departingDuration.minutes + returningDuration.hours * 60 + returningDuration.minutes
}

function getDepartureTimeBand(journey: App.JourneyV2): number {
  const firstFlight = journey.flightGroup.flights[0]
  const departureHour = Number((firstFlight?.departingTime ?? firstFlight?.departureDateTime?.split(' ')[1])?.split(':')[0])

  if (departureHour >= 9 && departureHour < 15) return 0
  if ((departureHour >= 5 && departureHour < 9) || (departureHour >= 15 && departureHour < 18)) return 1
  if (departureHour >= 18 && departureHour < 21) return 2
  if (departureHour >= 21) return 3
  return 4
}

function getCombinedDepartureTimeBand(journey: App.FlightCombinedJourney): number {
  return getDepartureTimeBand(journey.departingJourney) + getDepartureTimeBand(journey.returningJourney)
}

function getCombinedStableJourneyKey(journey: App.FlightCombinedJourney): string {
  const departingKey = journey.departingJourney.journeyKey ?? journey.departingJourney.id ?? journey.departingJourney.bookingInfo.boookingKey
  const returningKey = journey.returningJourney.journeyKey ?? journey.returningJourney.id ?? journey.returningJourney.bookingInfo.boookingKey
  return `${departingKey}-${returningKey}`
}

function domesticCombinedJourneysComparator(journeyA: App.FlightCombinedJourney, journeyB: App.FlightCombinedJourney): number {
  const totalStopsA = getCombinedJourneyStops(journeyA)
  const totalStopsB = getCombinedJourneyStops(journeyB)
  if (totalStopsA > totalStopsB) return 1
  if (totalStopsA < totalStopsB) return -1

  const totalPriceA = getCombinedJourneyPrice(journeyA)
  const totalPriceB = getCombinedJourneyPrice(journeyB)
  if (totalPriceA > totalPriceB) return 1
  if (totalPriceA < totalPriceB) return -1

  const totalDurationA = getCombinedJourneyDuration(journeyA)
  const totalDurationB = getCombinedJourneyDuration(journeyB)
  if (totalDurationA > totalDurationB) return 1
  if (totalDurationA < totalDurationB) return -1

  const totalDepartureTimeBandA = getCombinedDepartureTimeBand(journeyA)
  const totalDepartureTimeBandB = getCombinedDepartureTimeBand(journeyB)
  if (totalDepartureTimeBandA > totalDepartureTimeBandB) return 1
  if (totalDepartureTimeBandA < totalDepartureTimeBandB) return -1

  return getCombinedStableJourneyKey(journeyA).localeCompare(getCombinedStableJourneyKey(journeyB))
}

export function sortDomesticCombinedFlightJourneys(journeys: Array<App.FlightCombinedJourney>): Array<App.FlightCombinedJourney> {
  return [...journeys].sort(domesticCombinedJourneysComparator)
}

export function selectDomesticCombinedJourneyShortlist(
  journeys: Array<App.FlightCombinedJourney>,
): Array<App.FlightCombinedJourney> {
  const shortlistedJourneys: Array<App.FlightCombinedJourney> = []
  const departingCarrierCounts = new Map<string, number>()
  const returningCarrierCounts = new Map<string, number>()

  for (const journey of journeys) {
    if (shortlistedJourneys.length === DOMESTIC_COMBINED_JOURNEY_SHORTLIST_SIZE) break

    const departingCarrier = journey.departingJourney.carrier
    const returningCarrier = journey.returningJourney.carrier
    const departingCarrierCount = departingCarrierCounts.get(departingCarrier) ?? 0
    const returningCarrierCount = returningCarrierCounts.get(returningCarrier) ?? 0

    if (
      departingCarrierCount >= MAX_DOMESTIC_COMBINED_JOURNEYS_PER_CARRIER ||
      returningCarrierCount >= MAX_DOMESTIC_COMBINED_JOURNEYS_PER_CARRIER
    ) continue

    shortlistedJourneys.push(journey)
    departingCarrierCounts.set(departingCarrier, departingCarrierCount + 1)
    returningCarrierCounts.set(returningCarrier, returningCarrierCount + 1)
  }

  return shortlistedJourneys
}
